import React, { useEffect, useMemo } from "react";
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Card,
  Spin,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Space,
  Tooltip,
  Collapse,
  Tag,
  Select,
  message,
} from "antd";
import {
  MailOutlined,
  CloudOutlined,
  ExperimentOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
  ApiOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import useParameters from "./useParameters";
import { ParameterInput, SystemParameter, testSMTPConnection } from "../../services/parameter.service";
import "./SystemParameters.css";

const { Title, Text } = Typography;

// ─── Category display config ──────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<
  string,
  {
    label: string;
    icon: React.ReactNode;
    description: string;
    order: number;
    group: "sistema" | "paises" | "avanzado";
  }
> = {
  SMTP: {
    label: "Servidor de Correo",
    icon: <MailOutlined />,
    description: "Configuración del servidor SMTP para envío automático de boletines.",
    order: 1,
    group: "sistema",
  },
  smtp: {
    label: "Servidor de Correo",
    icon: <MailOutlined />,
    description: "Configuración del servidor SMTP para envío automático de boletines.",
    order: 1,
    group: "sistema",
  },
  Copernicus: {
    label: "Copernicus Marine",
    icon: <CloudOutlined />,
    description: "Credenciales para acceder a datos satelitales oceanográficos.",
    order: 2,
    group: "sistema",
  },
  "Copernicus ADS": {
    label: "Copernicus Atmosphere",
    icon: <CloudOutlined />,
    description: "Credenciales para acceder a datos atmosféricos de Copernicus ADS.",
    order: 2.5,
    group: "sistema",
  },
  Credenciales: {
    label: "Servicios Externos",
    icon: <ApiOutlined />,
    description: "Configuración de acceso a Zunibal FTP, eLogbook y otros servicios de terceros.",
    order: 3,
    group: "sistema",
  },
  Scheduler: {
    label: "Programación",
    icon: <ClockCircleOutlined />,
    description: "Horarios de ejecución automática de boletines y tareas.",
    order: 3,
    group: "sistema",
  },
  "Boletines Oceanograficos": {
    label: "Boletines Oceanográficos",
    icon: <ExperimentOutlined />,
    description: "Configuración general de generación y distribución de boletines.",
    order: 4,
    group: "sistema",
  },
  "Peru Config": {
    label: "🇵🇪  Perú",
    icon: <GlobalOutlined />,
    description: "Coordenadas geográficas, puntos de control y configuración para Perú.",
    order: 5,
    group: "paises",
  },
  "Ecuador Config": {
    label: "🇪🇨  Ecuador",
    icon: <GlobalOutlined />,
    description: "Coordenadas geográficas y puntos de control para Ecuador.",
    order: 6,
    group: "paises",
  },
  "Panama Config": {
    label: "🇵🇦  Panamá",
    icon: <GlobalOutlined />,
    description: "Coordenadas geográficas y puntos de control para Panamá.",
    order: 7,
    group: "paises",
  },
  "Oman Config": {
    label: "🇴🇲  Omán",
    icon: <GlobalOutlined />,
    description: "Coordenadas geográficas y puntos de control para Omán.",
    order: 8,
    group: "paises",
  },
  credentials: {
    label: "Credenciales FTP / API",
    icon: <SafetyCertificateOutlined />,
    description: "Credenciales del servidor FTP de Zunibal y API eLogbook.",
    order: 9,
    group: "avanzado",
  },
  pipeline: {
    label: "Pipeline Acústico",
    icon: <SettingOutlined />,
    description: "Configuración del pipeline de boletines acústicos.",
    order: 11,
    group: "avanzado",
  },
};

/**
 * Categories hidden from the UI — they exist in the DB for legacy reasons
 * but are no longer used by any active service.
 * Keys: smtp_host, smtp_port, smtp_user, smtp_password, smtp_security
 * Active email config is now under category "SMTP" with keys smtp.host, smtp.port, etc.
 */
const HIDDEN_CATEGORIES = new Set(["smtp", "general", "ocean_bulletins", "Ocean Bulletin"]);

/**
 * Parameter keys hidden from the UI — they exist in the DB for legacy reasons
 * but are no longer used by any active service.
 */
const HIDDEN_KEYS = new Set([
  "ocean.peru.special_recipients",
  "ocean.peru.special_send_day",
  "ocean.scheduler.cron_hour",
  "ocean.scheduler.cron_minute",
  "ocean.bulletin.special_day",
  "ocean.bulletin.special_emails",
  "smtp_host",
  "smtp_port",
  "smtp_user",
  "smtp_password",
  "smtp_security",
]);


const GROUP_META: Record<string, { label: string; color: string; description: string }> = {
  sistema: {
    label: "⚙️ Sistema",
    color: "#1890ff",
    description: "Credenciales, correo y programación del sistema",
  },
  paises: {
    label: "🌎 Países",
    color: "#52c41a",
    description: "Coordenadas y parámetros geográficos por país",
  },
  avanzado: {
    label: "🔧 Avanzado",
    color: "#fa8c16",
    description: "Integraciones legacy y pipelines acústicos",
  },
};

// ─── Field label definitions ──────────────────────────────────────────────────

const FIELD_LABELS: Record<string, { label: string; hint?: string; type?: string; group?: string; options?: {label: string, value: string}[] }> = {
  "copernicus.username": { label: "Usuario", hint: "Cuenta de Copernicus Marine Service" },
  "copernicus.password": { label: "Contraseña", hint: "Contraseña de tu cuenta CMEMS" },
  "copernicus_ads.url": { label: "URL del API", hint: "Por defecto: https://ads.atmosphere.copernicus.eu/api" },
  "copernicus_ads.key": { label: "API Key (Personal Access Token)", hint: "Formato: UID:API-KEY" },
  "ocean.bulletin.retention_days": { label: "Días de retención", type: "number", hint: "Archivos más antiguos se eliminan automáticamente" },
  "ocean.bulletin.auto_email": { label: "Envío automático por email", type: "boolean", hint: "Enviar boletín por email al generarlo" },
  "ocean.bulletin.email_subject_template": { label: "Plantilla del asunto", hint: "Variables: {country}, {date}" },
  "ocean.bulletin.pptx_to_jpg": { label: "Convertir a imagen", type: "boolean", hint: "Genera preview JPG de la primera slide" },
  "ocean.peru.enabled": { label: "Habilitado", type: "boolean", group: "General" },
  "ocean.peru.lon_min": { label: "Longitud mínima (°)", type: "number", hint: "Límite oeste", group: "Área de Análisis" },
  "ocean.peru.lon_max": { label: "Longitud máxima (°)", type: "number", hint: "Límite este", group: "Área de Análisis" },
  "ocean.peru.lat_min": { label: "Latitud mínima (°)", type: "number", hint: "Límite sur", group: "Área de Análisis" },
  "ocean.peru.lat_max": { label: "Latitud máxima (°)", type: "number", hint: "Límite norte", group: "Área de Análisis" },
  "ocean.peru.pto_control_name": { label: "Nombre del punto de control", hint: "Ej: Chicama", group: "Puntos de Control" },
  "ocean.peru.pto_control_lat": { label: "Latitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.peru.pto_control_lon": { label: "Longitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.peru.pto_analizar_lat": { label: "Latitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.peru.pto_analizar_lon": { label: "Longitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.peru.schedule_hour": { label: "🔧 Hora de creación del boletín", type: "number", hint: "Hora UTC (0-23). Perú = UTC-5, así que pon 5 horas más. Ej: para crear a las 4 AM local → pon 9", group: "⏰ Programación — Creación del Boletín" },
  "ocean.peru.schedule_minute": { label: "🔧 Minuto de creación", type: "number", hint: "Minuto (0-59) en que se creará el boletín", group: "⏰ Programación — Creación del Boletín" },
  "ocean.peru.dispatch_hour": { label: "📧 Hora de envío por email", type: "number", hint: "Hora UTC (0-23). Perú = UTC-5. Ej: para enviar a las 6 AM local → pon 11", group: "📧 Programación — Envío por Email" },
  "ocean.peru.dispatch_minute": { label: "📧 Minuto de envío", type: "number", hint: "Minuto (0-59) en que se enviará el email con el boletín ya generado", group: "📧 Programación — Envío por Email" },
  "ocean.peru.summary_day": { label: "📊 Día del mes", type: "number", hint: "Día del mes (1-28) en que se genera y envía el resumen mensual automáticamente", group: "📊 Resumen Mensual" },
  "ocean.peru.summary_hour": { label: "📊 Hora de envío (UTC)", type: "number", hint: "Hora UTC (0-23). Perú = UTC-5. Ej: para enviar al mediodía local → pon 17", group: "📊 Resumen Mensual" },
  "ocean.ecuador.enabled": { label: "Habilitado", type: "boolean", group: "General" },
  "ocean.ecuador.lon_min": { label: "Longitud mínima (°)", type: "number", hint: "Límite oeste", group: "Área de Análisis" },
  "ocean.ecuador.lon_max": { label: "Longitud máxima (°)", type: "number", hint: "Límite este", group: "Área de Análisis" },
  "ocean.ecuador.lat_min": { label: "Latitud mínima (°)", type: "number", hint: "Límite sur", group: "Área de Análisis" },
  "ocean.ecuador.lat_max": { label: "Latitud máxima (°)", type: "number", hint: "Límite norte", group: "Área de Análisis" },
  "ocean.ecuador.pto_control_name": { label: "Nombre del punto de control", hint: "Ej: Chanduy", group: "Puntos de Control" },
  "ocean.ecuador.pto_control_lat": { label: "Latitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.ecuador.pto_control_lon": { label: "Longitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.ecuador.pto_analizar_lat": { label: "Latitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.ecuador.pto_analizar_lon": { label: "Longitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.ecuador.schedule_hour": { label: "🔧 Hora de creación del boletín", type: "number", hint: "Hora UTC (0-23). Ecuador = UTC-5, así que pon 5 horas más. Ej: para crear a las 4 AM local → pon 9", group: "⏰ Programación — Creación del Boletín" },
  "ocean.ecuador.schedule_minute": { label: "🔧 Minuto de creación", type: "number", hint: "Minuto (0-59) en que se creará el boletín", group: "⏰ Programación — Creación del Boletín" },
  "ocean.ecuador.dispatch_hour": { label: "📧 Hora de envío por email", type: "number", hint: "Hora UTC (0-23). Ecuador = UTC-5. Ej: para enviar a las 6 AM local → pon 11", group: "📧 Programación — Envío por Email" },
  "ocean.ecuador.dispatch_minute": { label: "📧 Minuto de envío", type: "number", hint: "Minuto (0-59) en que se enviará el email con el boletín ya generado", group: "📧 Programación — Envío por Email" },
  "ocean.ecuador.summary_day": { label: "📊 Día del mes", type: "number", hint: "Día del mes (1-28) en que se genera y envía el resumen mensual automáticamente", group: "📊 Resumen Mensual" },
  "ocean.ecuador.summary_hour": { label: "📊 Hora de envío (UTC)", type: "number", hint: "Hora UTC (0-23). Ecuador = UTC-5. Ej: para enviar al mediodía local → pon 17", group: "📊 Resumen Mensual" },
  "ocean.panama.enabled": { label: "Habilitado", type: "boolean", group: "General" },
  "ocean.panama.lon_min": { label: "Longitud mínima (°)", type: "number", hint: "Límite oeste", group: "Área de Análisis" },
  "ocean.panama.lon_max": { label: "Longitud máxima (°)", type: "number", hint: "Límite este", group: "Área de Análisis" },
  "ocean.panama.lat_min": { label: "Latitud mínima (°)", type: "number", hint: "Límite sur", group: "Área de Análisis" },
  "ocean.panama.lat_max": { label: "Latitud máxima (°)", type: "number", hint: "Límite norte", group: "Área de Análisis" },
  "ocean.panama.pto_control_name": { label: "Nombre del punto de control", hint: "Ej: Chame", group: "Puntos de Control" },
  "ocean.panama.pto_control_lat": { label: "Latitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.panama.pto_control_lon": { label: "Longitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.panama.pto_analizar_lat": { label: "Latitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.panama.pto_analizar_lon": { label: "Longitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.panama.schedule_hour": { label: "🔧 Hora de creación del boletín", type: "number", hint: "Hora UTC (0-23). Panamá = UTC-5, así que pon 5 horas más. Ej: para crear a las 5 AM local → pon 10", group: "⏰ Programación — Creación del Boletín" },
  "ocean.panama.schedule_minute": { label: "🔧 Minuto de creación", type: "number", hint: "Minuto (0-59) en que se creará el boletín", group: "⏰ Programación — Creación del Boletín" },
  "ocean.panama.dispatch_hour": { label: "📧 Hora de envío por email", type: "number", hint: "Hora UTC (0-23). Panamá = UTC-5. Ej: para enviar a las 6 AM local → pon 11", group: "📧 Programación — Envío por Email" },
  "ocean.panama.dispatch_minute": { label: "📧 Minuto de envío", type: "number", hint: "Minuto (0-59) en que se enviará el email con el boletín ya generado", group: "📧 Programación — Envío por Email" },
  "ocean.panama.summary_day": { label: "📊 Día del mes", type: "number", hint: "Día del mes (1-28) en que se genera y envía el resumen mensual automáticamente", group: "📊 Resumen Mensual" },
  "ocean.panama.summary_hour": { label: "📊 Hora de envío (UTC)", type: "number", hint: "Hora UTC (0-23). Panamá = UTC-5. Ej: para enviar al mediodía local → pon 17", group: "📊 Resumen Mensual" },
  "ocean.oman.enabled": { label: "Habilitado", type: "boolean", group: "General" },
  "ocean.oman.lon_min": { label: "Longitud mínima (°)", type: "number", hint: "Límite oeste", group: "Área de Análisis" },
  "ocean.oman.lon_max": { label: "Longitud máxima (°)", type: "number", hint: "Límite este", group: "Área de Análisis" },
  "ocean.oman.lat_min": { label: "Latitud mínima (°)", type: "number", hint: "Límite sur", group: "Área de Análisis" },
  "ocean.oman.lat_max": { label: "Latitud máxima (°)", type: "number", hint: "Límite norte", group: "Área de Análisis" },
  "ocean.oman.pto_control_name": { label: "Nombre del punto de control", hint: "Ej: Ash Shuwaymiyah", group: "Puntos de Control" },
  "ocean.oman.pto_control_lat": { label: "Latitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.oman.pto_control_lon": { label: "Longitud Pto. Control (20mn)", type: "number", group: "Puntos de Control" },
  "ocean.oman.pto_analizar_lat": { label: "Latitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.oman.pto_analizar_lon": { label: "Longitud Pto. Análisis (Puerto)", type: "number", group: "Puntos de Control" },
  "ocean.oman.schedule_hour": { label: "🔧 Hora de creación del boletín", type: "number", hint: "Hora UTC (0-23). Omán = UTC+4, así que resta 4 horas. Ej: para crear a las 4 AM local → pon 0", group: "⏰ Programación — Creación del Boletín" },
  "ocean.oman.schedule_minute": { label: "🔧 Minuto de creación", type: "number", hint: "Minuto (0-59) en que se creará el boletín", group: "⏰ Programación — Creación del Boletín" },
  "ocean.oman.dispatch_hour": { label: "📧 Hora de envío por email", type: "number", hint: "Hora UTC (0-23). Omán = UTC+4. Ej: para enviar a las 6 AM local → pon 2", group: "📧 Programación — Envío por Email" },
  "ocean.oman.dispatch_minute": { label: "📧 Minuto de envío", type: "number", hint: "Minuto (0-59) en que se enviará el email con el boletín ya generado", group: "📧 Programación — Envío por Email" },
  "ocean.oman.summary_day": { label: "📊 Día del mes", type: "number", hint: "Día del mes (1-28) en que se genera y envía el resumen mensual automáticamente", group: "📊 Resumen Mensual" },
  "ocean.oman.summary_hour": { label: "📊 Hora de envío (UTC)", type: "number", hint: "Hora UTC (0-23). Omán = UTC+4. Ej: para enviar a las 10 AM local → pon 6", group: "📊 Resumen Mensual" },
  "ocean.scheduler.enabled": { label: "Generación automática", type: "boolean", hint: "Activar/desactivar la generación diaria de todos los boletines" },
  "zunibal_ftp_host": { label: "Host del servidor FTP", hint: "Ej: www.zunibal.com", group: "Zunibal" },
  "zunibal_ftp_port": { label: "Puerto FTP", hint: "Por defecto: 21", type: "number", group: "Zunibal" },
  "zunibal_ftp_user": { label: "Usuario FTP", group: "Zunibal" },
  "zunibal_ftp_pass": { label: "Contraseña FTP", group: "Zunibal" },
  "eloogbook_api_url": { label: "eLogbook API URL", hint: "Ej: http://data.elogbook.bitcliq.com", group: "eLogbook" },
  "eloogbook_api_key": { label: "API Key", group: "eLogbook" },
  "eloogbook_vessel_id": { label: "Vessel ID (Barco)", type: "number", group: "eLogbook" },
  "smtp.host": { label: "Servidor SMTP", hint: "Ej: smtp.gmail.com o smtp.ionos.es", group: "Conexión" },
  "smtp.port": { label: "Puerto SMTP", hint: "Ej: 465, 587, 25", type: "number", group: "Conexión" },
  "smtp.security": { label: "Cifrado de la conexión", type: "select", options: [{ label: "Ninguno", value: "none" }, { label: "TLS (STARTTLS)", value: "tls" }, { label: "SSL/TLS", value: "ssl" }], group: "Conexión" },
  "smtp.sender_email": { label: "Filtro DE", hint: "Convertir este email en remitente por defecto", group: "Autenticación" },
  "smtp.user": { label: "Nombre de usuario", hint: "Usuario para conectarse al servidor", group: "Autenticación" },
  "smtp.password": { label: "Contraseña", hint: "Contraseña de la cuenta", group: "Autenticación" },
  "pipeline_start_date": { label: "Fecha inicial de escaneo", hint: "Formato: YYYY/MM/DD", group: "Filtro de Datos" },
  "pipeline_schedule": { label: "Frecuencia de descarga (CRON)", hint: "Ej: 0 0 * * *", group: "Programación" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function renderField(param: SystemParameter) {
  const fieldConfig = FIELD_LABELS[param.key];
  const fieldType = fieldConfig?.type;
  if (fieldType === "boolean") return <Switch checkedChildren="Sí" unCheckedChildren="No" />;
  if (fieldType === "number") return <InputNumber style={{ width: "100%" }} />;
  if (fieldType === "select") return <Select options={fieldConfig.options} style={{ width: "100%" }} />;
  if (param.is_secret) return <Input.Password placeholder="••••••••" autoComplete="off" />;
  if (param.key.includes("recipients") || param.key.includes("email_body"))
    return <Input.TextArea rows={2} placeholder={fieldConfig?.hint || ""} />;
  return <Input placeholder={fieldConfig?.hint || ""} />;
}

function getLabel(param: SystemParameter): string {
  return FIELD_LABELS[param.key]?.label || param.key;
}

/** Build Collapse items array for a list of categories (antd v5 API). */
function buildCollapseItems(
  cats: string[],
  parameters: SystemParameter[]
): NonNullable<React.ComponentProps<typeof Collapse>["items"]> {
  return cats.map((cat) => {
    const normalizedCat = cat.trim();
    const catConfig = CATEGORY_CONFIG[normalizedCat] ?? {
      label: normalizedCat,
      icon: <SettingOutlined />,
      description: "",
      order: 99,
      group: "avanzado" as const,
    };
    const catParams = parameters.filter((p) => p.category === cat && !HIDDEN_KEYS.has(p.key));
    const secretCount = catParams.filter((p) => p.is_secret).length;
    const configuredCount = catParams.filter((p) => p.value && p.value !== "").length;

    // Sub-group fields within the category
    const subGroups: Record<string, SystemParameter[]> = {};
    catParams.forEach((p) => {
      const grp = FIELD_LABELS[p.key]?.group || "General";
      if (!subGroups[grp]) subGroups[grp] = [];
      subGroups[grp].push(p);
    });
    const subGroupNames = Object.keys(subGroups);

    return {
      key: cat,
      label: (
        <Space>
          {catConfig.icon}
          <span style={{ fontWeight: 600 }}>{catConfig.label}</span>
          <Badge
            count={`${configuredCount}/${catParams.length}`}
            style={{
              backgroundColor: configuredCount === catParams.length ? "#52c41a" : "#faad14",
              fontSize: 10,
            }}
          />
        </Space>
      ),
      children: (
        <div>
          {catConfig.description && (
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              {catConfig.description}
            </Text>
          )}

          {secretCount > 0 && (
            <div
              style={{
                background: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 8,
                padding: "8px 16px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <SafetyCertificateOutlined style={{ color: "#fa8c16" }} />
              <Text style={{ color: "#ad6800", fontSize: 13 }}>
                Esta categoría contiene {secretCount} campo(s) sensible(s). Se almacenan cifrados.
              </Text>
            </div>
          )}

          {subGroupNames.map((groupName) => (
            <div key={groupName}>
              {subGroupNames.length > 1 && (
                <Divider orientation="left" style={{ margin: "16px 0 12px" }}>
                  <Text strong style={{ color: "#595959" }}>
                    {groupName}
                  </Text>
                </Divider>
              )}
              <Row gutter={[24, 8]}>
                {subGroups[groupName].map((p) => (
                  <Col xs={24} sm={24} md={12} lg={12} key={p.key}>
                    <Form.Item
                      label={
                        <Space size={4}>
                          <Text strong style={{ fontSize: 13 }}>
                            {getLabel(p)}
                          </Text>
                          {FIELD_LABELS[p.key]?.hint && (
                            <Tooltip title={FIELD_LABELS[p.key]?.hint}>
                              <InfoCircleOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
                            </Tooltip>
                          )}
                        </Space>
                      }
                      name={p.key}
                      tooltip={p.description}
                      style={{ marginBottom: 12 }}
                      valuePropName={FIELD_LABELS[p.key]?.type === "boolean" ? "checked" : "value"}
                    >
                      {renderField(p)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              {cat === "SMTP" && groupName === "Autenticación" && (
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="primary"
                    style={{ background: "#722ed1", borderColor: "#722ed1" }}
                    onClick={async () => {
                      const formValues = (window as any).__system_params_form?.getFieldsValue() || {};
                      const hide = message.loading("Probando conexión SMTP...", 0);
                      try {
                        await testSMTPConnection(formValues);
                        hide();
                        message.success("Conexión exitosa al servidor SMTP");
                      } catch (err: any) {
                        hide();
                        message.error(err.response?.data?.error || "Error al probar la conexión");
                      }
                    }}
                  >
                    Probar conexión
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ),
    };
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SystemParameters() {
  const { parameters, loading, saving, saveParameters } = useParameters();
  const [form] = Form.useForm();
  
  // Hack to allow "Probar conexión" button to access form
  if (typeof window !== "undefined") {
    (window as any).__system_params_form = form;
  }

  useEffect(() => {
    if (parameters.length > 0) {
      const initialValues: Record<string, any> = {};
      parameters.forEach((p) => {
        const fieldConfig = FIELD_LABELS[p.key];
        if (fieldConfig?.type === "boolean") {
          initialValues[p.key] = p.value === "true" || p.value === "1";
        } else if (fieldConfig?.type === "number") {
          initialValues[p.key] = p.value ? parseFloat(p.value) : undefined;
        } else {
          initialValues[p.key] = p.value;
        }
      });
      form.setFieldsValue(initialValues);
    }
  }, [parameters, form]);

  const onFinish = (values: Record<string, any>) => {
    const paramsToUpdate: ParameterInput[] = [];
    parameters.forEach((p) => {
      if (values[p.key] !== undefined) {
        let val = values[p.key];
        if (typeof val === "boolean") val = val.toString();
        else if (typeof val === "number") val = val.toString();
        else if (val === null || val === undefined) val = "";
        paramsToUpdate.push({ key: p.key, value: val, category: p.category, is_secret: p.is_secret, description: p.description });
      }
    });
    saveParameters(paramsToUpdate);
  };

  /** Returns { sistema: string[], paises: string[], avanzado: string[] } sorted by order */
  const groupedCategories = useMemo(() => {
    const cats = Array.from(new Set(parameters.map((p) => p.category)))
      .filter((cat) => !HIDDEN_CATEGORIES.has(cat.trim())) // hide legacy categories
      .filter((cat) => parameters.some((p) => p.category === cat && !HIDDEN_KEYS.has(p.key))); // hide empty categories
    const sorted = cats.sort((a, b) => {
      const oA = CATEGORY_CONFIG[a.trim()]?.order ?? 99;
      const oB = CATEGORY_CONFIG[b.trim()]?.order ?? 99;
      return oA - oB;
    });
    const result: Record<"sistema" | "paises" | "avanzado", string[]> = { sistema: [], paises: [], avanzado: [] };
    sorted.forEach((cat) => {
      const grp = CATEGORY_CONFIG[cat.trim()]?.group ?? "avanzado";
      result[grp].push(cat);
    });
    return result;
  }, [parameters]);

  if (loading && parameters.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderGroup = (groupKey: "sistema" | "paises" | "avanzado") => {
    const cats = groupedCategories[groupKey];
    if (!cats || cats.length === 0) return null;

    const meta = GROUP_META[groupKey];
    const totalParams = cats.reduce((s, c) => s + parameters.filter((p) => p.category === c).length, 0);
    const cfgParams = cats.reduce(
      (s, c) => s + parameters.filter((p) => p.category === c && p.value && p.value !== "").length,
      0
    );

    return (
      <div key={groupKey} style={{ marginBottom: 28 }}>
        {/* Group header bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 0 10px",
            borderBottom: `2px solid ${meta.color}33`,
            marginBottom: 10,
          }}
        >
          <Tag
            color={meta.color}
            style={{ fontWeight: 700, fontSize: 13, padding: "2px 10px", margin: 0 }}
          >
            {meta.label}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {meta.description}
          </Text>
          <Badge
            count={`${cfgParams}/${totalParams}`}
            style={{
              backgroundColor: cfgParams === totalParams ? "#52c41a" : "#faad14",
              marginLeft: "auto",
            }}
          />
        </div>

        {/* Collapse with items API (antd v5) */}
        <Collapse
          items={buildCollapseItems(cats, parameters)}
          ghost
          style={{ background: "transparent" }}
        />
      </div>
    );
  };

  return (
    <div className="system-parameters-container">
      <div className="system-parameters-header">
        <div>
          <Title level={2} style={{ margin: 0 }}>
            ⚙️ Parámetros del Sistema
          </Title>
          <Text type="secondary">
            Gestiona integraciones, credenciales y parámetros operativos de Albor.
          </Text>
        </div>
      </div>

      <Card className="system-parameters-card">
        <Form form={form} layout="vertical" onFinish={onFinish} disabled={saving}>
          {renderGroup("sistema")}
          {renderGroup("paises")}
          {renderGroup("avanzado")}

          <Divider />

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => form.resetFields()} disabled={saving}>
              Restablecer
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{ background: "#81BC3D", borderColor: "#81BC3D", borderRadius: 8, padding: "0 32px" }}
            >
              💾 Guardar Cambios
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}
